import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { ApiService } from "../../../shared/api.service";
import { filter, tap } from "rxjs";
import { Membership, MembershipData, MembershipResponse } from './membership.types';
import { formatDateToReadableString } from "../../../shared/utils/formatting";
import { NotificationService } from "../../../shared/notification/notification.service";
import { RequestHandlerParams } from "../../../shared/types/api-service.types";
import { Session } from "../../../shared/types/session.type";
import { LocalStorageService } from "../../../shared/local-storage.service";
import { LOCALSTORAGE_KEYS } from "../../../shared/constants/databases";
import { ModalContentService } from "../../../shared/modal-content/modal-content.service";
import { ModalContentData } from "../../../shared/modal-content/modal-content";
import { ModalContentTypes } from "../../../shared/constants/modal-content-types";
import { MembershipUpgradePlan } from "../../../shared/types/order.type";
import { SignalsStoreService } from "../../../shared/signals-store.service";
import { OrderService } from "../../../shared/order.service";


@Injectable()
export class MembershipService {
  private apiService = inject(ApiService);
  private notificationService = inject(NotificationService);
  private localStorageService = inject(LocalStorageService);
  private modalContentService = inject(ModalContentService);
  private signalsStoreService = inject(SignalsStoreService);
  private orderService = inject(OrderService);

  membership: WritableSignal<Membership | null> = signal(null);
  membershipList: WritableSignal<MembershipData[]> = signal([]);
  loading: WritableSignal<boolean> = signal(false);


  private endpoints = {
    base: '/account/membership',
  }

  subscribe(deposit: number = 0, renew: boolean = false, membershipId: number) {
    const params: RequestHandlerParams = {
      method: 'POST',
      endpoint: `${this.endpoints.base}/${membershipId}`,
      body: { deposit, renew },
      apiV3: true
    };

    this.apiService.handleRequest(params).pipe(
      filter((response: any) => !!response),
      tap((response: any) => this.notificationService.show({ text: response.message, type: 'success' })),
      tap(() => {
        const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any;
        let settings = sessionStored.settings;
        settings.hasMembership = true;
        const newSessionStored: Session = { ...sessionStored, settings };
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, newSessionStored);
        // Everything that gates on membership reads sessionSignal, not localStorage,
        // so without this the UI keeps treating the customer as a non-member until
        // the next full page load.
        this.signalsStoreService.sessionSignal.set(newSessionStored);
      }),
      tap((response: any) => this.setUpMembershipDetailsResponse(response)),
      tap(() => this.get()),
      // Re-fetch the order so member pricing and the savings line reflect the new
      // membership before checkout (DEV-10551).
      tap(() => this.orderService.getOrder())
    ).subscribe();
  }

  unsubscribe() {
    const params: RequestHandlerParams = {
      method: 'DELETE',
      endpoint: `${this.endpoints.base}`,
      apiV3: true
    };

    this.apiService.handleRequest(params).pipe(
      filter((response: any) => !!response),
      tap(() => this.notificationService.show({ text: 'We are sad to see you go but your membership cancelation has been saved', type: 'success' })),
      tap(() => this.membership.set(null)),
      tap(() => this.get())
    ).subscribe();
  }

  get() {
    this.loading.set(true);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `${this.endpoints.base}`,
      apiV3: true
    };

    this.apiService.handleRequest(params).pipe(
      filter((response: any) => !!response),
      tap((response: MembershipResponse) => this.setUpMembershipDetailsResponse(response)),
      tap(() => this.loading.set(false))
    ).subscribe();
  }

  private setUpMembershipDetailsResponse(response: MembershipResponse, validateList: boolean = true): void {

    const { list, userMembership } = response;
    if (list && validateList) {
      this.membershipList.set(list ? list.sort((a, b) => a.order - b.order).map((membership: MembershipData, index: number) => {
        membership.order = (index + 1);
        membership.cardCost = signal(+membership.cost);
        membership.cardStandard = membership.cost === 0;
        membership.renewalType = membership.renewalType.toLowerCase();
        return membership;
      }) : []);
    }

    if (userMembership?.id) {
      userMembership.nextBillingDate = userMembership.nextBillingDate ? formatDateToReadableString(userMembership.nextBillingDate).readableString : '';
      userMembership.since = userMembership.since ? formatDateToReadableString(userMembership.since).readableString : '';
      userMembership.endDate = userMembership.endDate ? formatDateToReadableString(userMembership.endDate).readableString : '';
      const userMembershipId = userMembership.id;
      // Match the customer's plan by fee level id. The previous lookup compared
      // `order` — a 1-based display index assigned just above — against an id, so it
      // never matched and every membership was enriched with the first card's
      // benefits, cost and name. Falls back to that card when the ids do not line up,
      // which keeps single-level brands rendering exactly as before.
      const matchingMembership =
        this.membershipList().find(membership => membership.id === userMembershipId)
        ?? this.membershipList().find(membership => membership.order === 1);
      if (matchingMembership) {
        userMembership.cardBenefits = matchingMembership.benefits;
        userMembership.cardStandard = matchingMembership.cardStandard;
        userMembership.cardCost = userMembership.membershipCost ? signal(userMembership.membershipCost ?? 0) : matchingMembership.cardCost;
        userMembership.cardImage = matchingMembership.image;
        userMembership.name = matchingMembership.name;
        userMembership.renewalType = userMembership.renewalType.toLowerCase();
      }
      this.membership.set(userMembership);
    }
  }

  /**
   * Streamlined upgrade flow reusable from the cart / order summary (DEV-10551).
   * Branches on `allowCSADeposit`: shows the CSA deposit step first when the
   * plan allows it, otherwise goes straight to the become-a-member confirmation.
   */
  openUpgradeFlow(plan: MembershipUpgradePlan): void {
    if (plan.allowCSADeposit) {
      this.#openDepositModal(plan);
      return;
    }
    this.#openBecomeAMemberModal(0, plan);
  }

  #openDepositModal(plan: MembershipUpgradePlan): void {
    this.modalContentService.openModal(ModalContentTypes.MEMBERSHIP_CSA_DEPOSITS, {
      title: 'Want to add a one time CSA deposit?'
    }).closed.subscribe((result: { deposit: number } | null) => {
      if (result && result.deposit >= 0)
        this.#openBecomeAMemberModal(result.deposit, plan);
    });
  }

  #openBecomeAMemberModal(deposit: number, plan: MembershipUpgradePlan): void {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session) return;
    this.modalContentService.openModal(ModalContentTypes.BECOME_A_MEMBER, {
      title: 'Become a member',
      becomeAMemeber: {
        membershipCost: plan.cost,
        creditCardLastNumbers: session?.creditCard?.number?.toString(),
        deposit,
        membershipFrequency: plan.renewalType,
        membershipName: plan.name
      }
    } as ModalContentData).closed.subscribe((result: { deposit: number } | null) => {
      if (result && result.deposit >= 0)
        this.subscribe(result.deposit, false, plan.id);
    });
  }
}
